import { useEffect, useState } from "react";
import { getContractDetails } from "../setup/api/services/contract";
import { SourceContractIdentity } from "../setup/util/backToSourceCashout";

/**
 * Resolves both of a source contract's ids, as a *fallback* for when comparing
 * a party's `referenceContractId` to `contract.sourceContractId` directly finds
 * no match — today it never does, because the contract carries the source's
 * `shortId` while the party carries its Mongo id, and fetching the source
 * contract is the only way to learn the missing one.
 *
 * Pass `undefined` to skip the request: callers should only resolve after the
 * direct comparison fails, so if the backend ever aligns the two shapes this
 * hook stops firing on its own. `getContractDetails` accepts either id shape,
 * so whichever one the contract carries is enough to look it up.
 */

/** Source contracts never change identity, so resolutions are cached per session. */
const identityCache = new Map<string, SourceContractIdentity>();

export interface UseSourceContractReturn {
  sourceContract?: SourceContractIdentity;
  isResolvingSourceContract: boolean;
}

export const useSourceContract = (
  sourceContractId?: string
): UseSourceContractReturn => {
  const [identity, setIdentity] = useState<SourceContractIdentity | undefined>(
    () => (sourceContractId ? identityCache.get(sourceContractId) : undefined)
  );
  // Starts true for an unresolved source so callers never read an "unresolved"
  // identity on the first render, before the effect has run.
  const [loading, setLoading] = useState<boolean>(
    () => !!sourceContractId && !identityCache.has(sourceContractId)
  );

  useEffect(() => {
    if (!sourceContractId) {
      setIdentity(undefined);
      setLoading(false);
      return;
    }

    const cached = identityCache.get(sourceContractId);
    if (cached) {
      setIdentity(cached);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    getContractDetails(sourceContractId)
      .then((response) => {
        const resolvedIdentity: SourceContractIdentity = {
          id: response?.data?.id,
          shortId: response?.data?.shortId,
        };
        identityCache.set(sourceContractId, resolvedIdentity);
        if (active) setIdentity(resolvedIdentity);
      })
      .catch((error) => {
        console.error("Error resolving source contract:", error);
        // Not cached — the failure is transient. The identity stays empty, so
        // nothing matches against it and callers keep the action hidden rather
        // than offering a refund the API will reject.
        if (active) setIdentity({});
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [sourceContractId]);

  return {
    sourceContract: identity,
    isResolvingSourceContract: loading,
  };
};
